import React, { useCallback, useEffect, useMemo } from 'react';
import { List, Input, Divider, Skeleton, Button } from 'antd';
import { isEmpty, omit } from 'lodash';
import { SearchOutlined, CheckOutlined } from '@ant-design/icons';
import InfiniteScroll from 'react-infinite-scroll-component';
import cn from 'classnames';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import debounce from 'lodash/debounce';
import useSearcher from './useSearcher';
import classes from './Style.module.scss';
import SupplierListItem from '../ListItem/SupplierListItem';

export default function SupplierSearcher({
  otherSource,
  multiple,
  selected,
  onChange,
  onBindingValue,
}) {
  const prevSelected = React.useRef([]);
  const prevSearch = React.useRef('');
  const [searchInput, setSearchInput] = React.useState('');
  const { loading, params, data, hasMore, updateParams, fetchData } = useSearcher({
    resourceName: 'suppliers',
    lazyLoad: true,
    transformParams: useCallback((currentParams) => {
      const defaultParams = {
        pageNo: currentParams.page,
        pageSize: currentParams.pageSize,
        status: 'active',
        // connectionStatus: 'connected',
      };
      if (currentParams.keywords) {
        defaultParams.searchBy = 'all';
        defaultParams.keywords = currentParams.keywords;
      }
      return defaultParams;
    }, []),
    hasMore: useCallback((current, pagination) => {
      return current.length < pagination.total;
    }, []),
  });

  const productSelectedSupplierGroup = useMemo(() => {
    if (Array.isArray(otherSource) && otherSource.length) {
      return otherSource.map((item) => {
        return {
          ...item,
          selected: selected.some(({ id }) => id === item.id),
        };
      });
    }
    return [];
  }, [otherSource, selected]);

  const allSupplierGroup = useMemo(() => {
    if (isEmpty(data)) {
      return [];
    }
    if (productSelectedSupplierGroup.length) {
      const productSelectedIds = productSelectedSupplierGroup.map(({ id }) => id);
      return data
        .filter(({ id }) => !productSelectedIds.includes(id))
        .map((item) => {
          return {
            ...item,
            selected: selected.some(({ id }) => id === item.id),
          };
        });
    }
    return data.map((item) => {
      return {
        ...item,
        selected: selected.some(({ id }) => id === item.id),
      };
    });
  }, [data, productSelectedSupplierGroup, selected]);

  const grouped = useMemo(() => {
    return [
      {
        key: 'product-selected-supplier',
        title: T.translate('selectedProductSupplier'),
        type: 'group',
        items: productSelectedSupplierGroup,
      },
      {
        key: 'all-supplier',
        title: T.translate('allSupplier'),
        type: 'group',
        items: allSupplierGroup,
      },
    ];
  }, [allSupplierGroup, productSelectedSupplierGroup]);

  const handleSelect = (initialSelected, item) => {
    const newData = [...initialSelected];
    const index = newData.findIndex(({ id }) => id === item.id);
    if (multiple) {
      if (index < 0) {
        newData.push({ ...item, selected: true });
      } else {
        newData.splice(index, 1);
      }
      return newData;
    }
    if (index < 0) {
      return [{ ...item, selected: true }];
    }
    newData.splice(index, 1);
    return newData;
  };

  const onToggleSelect = (item) => {
    prevSelected.current = handleSelect(prevSelected.current, item);
    onChange(prevSelected.current.map((_item) => omit(_item, 'selected')));
  };

  const renderItem = (item) => {
    const bindValue = onBindingValue ? onBindingValue(item) : item;
    return (
      <SupplierListItem
        bordered={false}
        name={bindValue.name}
        supplierId={bindValue.supplierId}
        address={bindValue.address || bindValue.address1 || bindValue.address2}
        contactPerson={bindValue.contactPerson}
        contactPersonPhone={bindValue.contactPersonPhone}
        emails={bindValue.supplierEmails}
        status={bindValue.status}
        className={cn(classes.listItem, {
          [classes.listItemSelected]: item.selected,
        })}
        key={item.id}
        extra={
          <Button
            icon={item.selected ? <CheckOutlined /> : undefined}
            type="primary"
            ghost={!item.selected}
            onClick={(e) => {
              e.preventDefault();
              // const itemIndex = allSupplierGroup.findIndex(({ id }) => id === item.id);
              onToggleSelect(item);
            }}>
            {item.selected ? T.translate('general.selected') : T.translate('general.select')}
          </Button>
        }
      />
    );
  };

  const debouncedSearch = useCallback(
    debounce((term) => {
      if (prevSearch.current !== term) {
        prevSearch.current = term;
        updateParams({ page: 1, keywords: term });
      }
    }, 250),
    []
  );

  const handleSearchChange = (e) => {
    // updateParams({ page: 1, keywords: e.target.value });
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  useEffect(() => {
    fetchData();
  }, [params.keywords]);

  useEffect(() => {
    prevSelected.current = selected;
    return () => {
      prevSelected.current = [];
    };
  }, [selected]);
  return (
    <div className={classes.suppliers}>
      <div>
        <Input
          size="large"
          placeholder={T.translate('searchBySupplierNameAndId')}
          prefix={<SearchOutlined />}
          onChange={handleSearchChange}
          value={searchInput}
        />
      </div>
      <InfiniteScroll
        className={cn('icw-scrollbar', classes.infiniteScroll)}
        next={() => {
          if (!loading) {
            fetchData();
          }
        }}
        hasMore={hasMore}
        dataLength={isEmpty(data) ? 0 : data.length}
        height={450}
        loader={<Skeleton paragraph={{ rows: 5 }} active />}>
        <List
          locale={!loading ? undefined : { emptyText: ' ' }}
          dataSource={grouped.filter(({ items }) => !!items.length)}
          renderItem={(record) => {
            if (record.type === 'group') {
              return (
                <React.Fragment>
                  <List.Item className={classes.listItemGroup}>{record.title}</List.Item>
                  {record.items.map((item, index) => {
                    return renderItem(item, record, index);
                  })}
                </React.Fragment>
              );
            }
            return renderItem(record);
          }}
        />
      </InfiniteScroll>
    </div>
  );
}
// Add prop type validation for 'type'
SupplierSearcher.propTypes = {
  otherSource: PropTypes.array,
  selected: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onBindingValue: PropTypes.func,
};
SupplierSearcher.defaultProps = {
  otherSource: [],
  selected: [],
  onBindingValue: null,
};
